import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { 
  QrCode, 
  ArrowLeft, 
  Download,
  Upload,
  X,
  Check,
  Palette,
  Link as LinkIcon,
  Hash,
  Image as ImageIcon,
  FileZip
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

export default function QREditorPage() {
  const navigate = useNavigate();
  const { qrId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(qrId);
  const isMounted = useRef(true);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [formData, setFormData] = useState({
    project_id: searchParams.get("project") || "",
    slug: "",
    destination_url: "",
    foreground_color: "#003594",
    background_color: "#FFFFFF",
    error_correction: "H",
    quiet_zone: 4,
    logo_base64: null,
  });

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const projectsRes = await api.get("/projects");
      setProjects(projectsRes.data);

      if (isEditing) {
        const qrRes = await api.get(`/qrcodes/${qrId}`);
        setFormData({
          project_id: qrRes.data.project_id,
          slug: qrRes.data.slug,
          destination_url: qrRes.data.destination_url,
          foreground_color: qrRes.data.foreground_color,
          background_color: qrRes.data.background_color,
          error_correction: qrRes.data.error_correction || "H",
          quiet_zone: qrRes.data.quiet_zone,
          logo_base64: qrRes.data.logo_base64,
        });
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = useCallback(async () => {
    if (!formData.slug || !formData.project_id) return;
    
    setPreviewLoading(true);
    try {
      const response = await api.post("/qrcodes/preview-generate", {
        ...formData,
        destination_url: formData.destination_url || "https://example.com"
      });
      setPreview(response.data.preview);
    } catch (error) {
      // Silent fail
    } finally {
      setPreviewLoading(false);
    }
  }, [formData]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (formData.slug && formData.project_id) {
        generatePreview();
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [formData.slug, formData.foreground_color, formData.background_color, formData.quiet_zone, formData.logo_base64, formData.project_id, generatePreview]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor sube un archivo de imagen");
      return;
    }

    if (file.size > 500000) {
      toast.error("El logo debe ser menor a 500KB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormData({ ...formData, logo_base64: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!formData.project_id) {
      toast.error("Por favor selecciona un proyecto");
      return;
    }
    if (!formData.slug) {
      toast.error("El slug es requerido");
      return;
    }
    if (!formData.destination_url) {
      toast.error("La URL de destino es requerida");
      return;
    }

    try {
      new URL(formData.destination_url);
    } catch {
      toast.error("Por favor ingresa una URL válida");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/qrcodes/${qrId}`, {
          destination_url: formData.destination_url,
          foreground_color: formData.foreground_color,
          background_color: formData.background_color,
          quiet_zone: formData.quiet_zone,
          logo_base64: formData.logo_base64,
        });
        toast.success("Código QR actualizado");
      } else {
        await api.post("/qrcodes", formData);
        toast.success("Código QR creado");
      }
      // Delay navigation to let toast render before unmount
      setTimeout(() => {
        if (isMounted.current) {
          navigate(`/projects/${formData.project_id}`);
        }
      }, 150);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al guardar";
      toast.error(message);
      if (isMounted.current) {
        setSaving(false);
      }
    }
  };

  const downloadQR = async (format) => {
    if (!isEditing) {
      toast.error("Guarda el código QR primero para descargar");
      return;
    }
    
    try {
      const response = await api.get(`/qrcodes/${qrId}/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', format === 'zip' ? `qr-${formData.slug}-assets.zip` : `qr-${formData.slug}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} descargado`);
    } catch (error) {
      toast.error("Error en la descarga");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#05152E' }}>
        <div className="w-12 h-12 border-4 border-t-transparent animate-spin" style={{ borderColor: '#008AD8', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dark-dashboard" style={{ backgroundColor: '#05152E' }} data-testid="qr-editor">
      {/* Header */}
      <header className="border-b sticky top-0 z-50" style={{ backgroundColor: '#05152E', borderColor: '#1A3A5C' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-[#0D2847] transition-colors"
              style={{ color: '#A1A1AA' }}
            >
              <ArrowLeft size={24} weight="bold" />
            </button>
            <div>
              <p className="uppercase text-[10px] tracking-[0.1em] font-medium" style={{ color: '#71717A' }}>{isEditing ? "Editar" : "Crear"}</p>
              <h1 className="text-xl font-black text-white">
                CÓDIGO QR
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isEditing && (
              <>
                <button
                  data-testid="download-zip-btn"
                  onClick={() => downloadQR('zip')}
                  className="flex items-center gap-2 px-4 py-2 text-white font-semibold transition-colors"
                  style={{ backgroundColor: '#008AD8' }}
                >
                  <FileZip size={18} weight="bold" />
                  Todos los Assets
                </button>
                <button
                  data-testid="download-svg-btn"
                  onClick={() => downloadQR('svg')}
                  className="flex items-center gap-2 px-4 py-2 border transition-colors"
                  style={{ borderColor: '#1A3A5C', color: '#A1A1AA' }}
                >
                  <Download size={18} />
                  SVG
                </button>
                <button
                  data-testid="download-png-btn"
                  onClick={() => downloadQR('png')}
                  className="flex items-center gap-2 px-4 py-2 border transition-colors"
                  style={{ borderColor: '#1A3A5C', color: '#A1A1AA' }}
                >
                  <Download size={18} />
                  PNG
                </button>
              </>
            )}
            <button
              data-testid="save-qr-btn"
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-white font-semibold hover:bg-[#F4F4F5] transition-colors"
              style={{ color: '#003594' }}
            >
              <Check size={18} weight="bold" />
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto flex flex-col lg:flex-row min-h-[calc(100vh-73px)]">
        {/* Left Panel */}
        <div className="lg:w-1/2 border-r overflow-y-auto" style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}>
          <div className="p-8 space-y-8">
            {/* Project */}
            <div>
              <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-3" style={{ color: '#71717A' }}>Proyecto</label>
              <Select
                value={formData.project_id}
                onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                disabled={isEditing}
              >
                <SelectTrigger 
                  data-testid="project-select"
                  className="w-full h-12 rounded-none border text-white"
                  style={{ borderColor: '#1A3A5C', backgroundColor: '#0D2847' }}
                >
                  <SelectValue placeholder="Selecciona un proyecto" />
                </SelectTrigger>
                <SelectContent className="rounded-none border" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
                  {projects.map((project) => (
                    <SelectItem 
                      key={project.project_id} 
                      value={project.project_id}
                      className="rounded-none text-white hover:bg-[#0D2847]"
                    >
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Slug */}
            <div>
              <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-3 flex items-center gap-2" style={{ color: '#71717A' }}>
                <Hash size={14} />
                Slug (URL Corta)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#71717A' }}>
                  /r/
                </span>
                <input
                  data-testid="slug-input"
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '')
                  })}
                  placeholder="mi-campaña"
                  className="flex-1 px-4 py-3 border text-white focus:outline-none transition-colors placeholder:text-[#71717A]"
                  style={{ fontFamily: 'IBM Plex Mono, monospace', borderColor: '#1A3A5C', backgroundColor: '#0D2847' }}
                  disabled={isEditing}
                />
              </div>
              {isEditing && (
                <p className="text-xs mt-2" style={{ color: '#71717A' }}>
                  El slug no puede cambiarse después de la creación
                </p>
              )}
            </div>

            {/* URL */}
            <div>
              <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-3 flex items-center gap-2" style={{ color: '#71717A' }}>
                <LinkIcon size={14} />
                URL de Destino
              </label>
              <input
                data-testid="url-input"
                type="url"
                value={formData.destination_url}
                onChange={(e) => setFormData({ ...formData, destination_url: e.target.value })}
                placeholder="https://ejemplo.com/pagina-destino"
                className="w-full px-4 py-3 border text-white focus:outline-none transition-colors placeholder:text-[#71717A]"
                style={{ fontFamily: 'IBM Plex Mono, monospace', borderColor: '#1A3A5C', backgroundColor: '#0D2847' }}
              />
              <p className="text-xs mt-2" style={{ color: '#008AD8' }}>
                Puedes cambiar esto en cualquier momento sin reimprimir el código QR
              </p>
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-3 flex items-center gap-2" style={{ color: '#71717A' }}>
                  <Palette size={14} />
                  Color de Frente
                </label>
                <div className="flex items-center gap-3">
                  <input
                    data-testid="fg-color-input"
                    type="color"
                    value={formData.foreground_color}
                    onChange={(e) => setFormData({ ...formData, foreground_color: e.target.value })}
                    className="w-12 h-12 border cursor-pointer bg-transparent"
                    style={{ borderColor: '#1A3A5C' }}
                  />
                  <input
                    type="text"
                    value={formData.foreground_color}
                    onChange={(e) => setFormData({ ...formData, foreground_color: e.target.value })}
                    className="flex-1 px-4 py-3 border text-white text-sm focus:outline-none transition-colors"
                    style={{ fontFamily: 'IBM Plex Mono, monospace', borderColor: '#1A3A5C', backgroundColor: '#0D2847' }}
                  />
                </div>
              </div>
              <div>
                <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-3 flex items-center gap-2" style={{ color: '#71717A' }}>
                  <Palette size={14} />
                  Color de Fondo
                </label>
                <div className="flex items-center gap-3">
                  <input
                    data-testid="bg-color-input"
                    type="color"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                    className="w-12 h-12 border cursor-pointer bg-transparent"
                    style={{ borderColor: '#1A3A5C' }}
                  />
                  <input
                    type="text"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                    className="flex-1 px-4 py-3 border text-white text-sm focus:outline-none transition-colors"
                    style={{ fontFamily: 'IBM Plex Mono, monospace', borderColor: '#1A3A5C', backgroundColor: '#0D2847' }}
                  />
                </div>
              </div>
            </div>

            {/* Quiet Zone */}
            <div>
              <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-3" style={{ color: '#71717A' }}>
                Zona Silenciosa (Margen para Impresión)
              </label>
              <div className="flex items-center gap-4">
                <Slider
                  data-testid="quiet-zone-slider"
                  value={[formData.quiet_zone]}
                  onValueChange={(value) => setFormData({ ...formData, quiet_zone: value[0] })}
                  min={1}
                  max={10}
                  step={1}
                  className="flex-1"
                />
                <span 
                  className="w-16 text-center font-bold text-white px-3 py-2 border"
                  style={{ fontFamily: 'IBM Plex Mono, monospace', borderColor: '#1A3A5C', backgroundColor: '#0D2847' }}
                >
                  {formData.quiet_zone}
                </span>
              </div>
              <p className="text-xs mt-2" style={{ color: '#71717A' }}>
                Valores más altos proporcionan mejor legibilidad en impresión. Recomendado: 4+ para gran formato.
              </p>
            </div>

            {/* Logo */}
            <div>
              <label className="uppercase text-[10px] tracking-[0.1em] font-medium block mb-3 flex items-center gap-2" style={{ color: '#71717A' }}>
                <ImageIcon size={14} />
                Logo Central (Opcional)
              </label>
              
              {formData.logo_base64 ? (
                <div className="flex items-center gap-4">
                  <img 
                    src={formData.logo_base64} 
                    alt="Vista previa del logo" 
                    className="w-16 h-16 object-contain border bg-white"
                    style={{ borderColor: '#1A3A5C' }}
                  />
                  <button
                    data-testid="remove-logo-btn"
                    onClick={() => setFormData({ ...formData, logo_base64: null })}
                    className="flex items-center gap-2 px-3 py-2 border transition-colors text-sm"
                    style={{ borderColor: '#1A3A5C', color: '#A1A1AA' }}
                  >
                    <X size={16} />
                    Eliminar
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center p-8 border border-dashed cursor-pointer transition-colors"
                  style={{ borderColor: '#1A3A5C', backgroundColor: '#0D2847' }}
                >
                  <Upload size={32} className="mb-2" style={{ color: '#71717A' }} />
                  <span className="text-sm" style={{ color: '#A1A1AA' }}>Subir Logo</span>
                  <span className="text-xs mt-1" style={{ color: '#71717A' }}>PNG, JPG hasta 500KB</span>
                  <input
                    data-testid="logo-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
              )}
              <p className="text-xs mt-2" style={{ color: '#008AD8' }}>
                El Nivel ECC H permite que hasta el 30% del código sea obscurecido por un logo
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="lg:w-1/2 lg:sticky lg:top-[73px] lg:h-[calc(100vh-73px)] flex flex-col" style={{ backgroundColor: '#0D2847' }}>
          <div className="flex-1 flex items-center justify-center p-8">
            <div 
              className="paper-texture p-8 max-w-md w-full aspect-square flex items-center justify-center"
              style={{ backgroundColor: formData.background_color }}
            >
              {previewLoading ? (
                <div className="w-16 h-16 border-4 border-t-transparent animate-spin" style={{ borderColor: '#008AD8', borderTopColor: 'transparent' }}></div>
              ) : preview ? (
                <img 
                  src={preview} 
                  alt="Vista Previa del Código QR" 
                  className="w-full h-full object-contain qr-animate-in"
                  data-testid="qr-preview-image"
                />
              ) : (
                <div className="text-center">
                  <QrCode size={64} weight="duotone" className="mx-auto mb-4" style={{ color: '#1A3A5C' }} />
                  <p className="text-sm" style={{ color: '#71717A' }}>
                    Ingresa un slug para ver la vista previa
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Preview Info */}
          <div className="p-6 border-t" style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Formato</p>
                <p className="font-bold text-sm text-white" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  SVG + PNG
                </p>
              </div>
              <div>
                <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Resolución</p>
                <p className="font-bold text-sm text-white" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  300 DPI
                </p>
              </div>
              <div>
                <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Nivel ECC</p>
                <p className="font-bold text-sm" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#008AD8' }}>
                  H (30%)
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
